/**
 * CodeEditorControl
 * Code editor using Ace Editor (ace-builds).
 * Falls back to a monospace textarea if ace-builds is unavailable.
 */

import React, { useCallback, useEffect, useRef } from 'react';
import type { FormControl } from '@atlas-forms/types-js';

export interface CodeEditorControlProps {
  control: FormControl;
  value?: unknown;
  onChange?: (value: any) => void;
  onBlur?: () => void;
  disabled?: boolean;
  mode?: string;
}

// Lazy-load ace to avoid hard dependency failure
let ace: any = null;
try {
  ace = require('ace-builds');
  require('ace-builds/src-noconflict/mode-javascript');
  require('ace-builds/src-noconflict/mode-typescript');
  require('ace-builds/src-noconflict/mode-python');
  require('ace-builds/src-noconflict/mode-json');
  require('ace-builds/src-noconflict/mode-css');
  require('ace-builds/src-noconflict/mode-html');
  require('ace-builds/src-noconflict/mode-sql');
  require('ace-builds/src-noconflict/theme-monokai');
  require('ace-builds/src-noconflict/theme-github');
} catch {
  ace = null;
}

const DEFAULT_HEIGHT = 240;

export function CodeEditorControl({
  control,
  value,
  onChange,
  onBlur,
  disabled,
  mode: formMode,
}: CodeEditorControlProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<any>(null);
  const language = (control.config?.language as string) || 'javascript';
  const height = ((control.config?.height as number) || DEFAULT_HEIGHT);
  const isReadOnly = formMode === 'view' || control.readonly || disabled || control.disabled;
  const strValue = typeof value === 'string' ? value : (value ? String(value) : '');

  useEffect(() => {
    if (!ace || !containerRef.current) return;

    const editor = ace.edit(containerRef.current);
    editorRef.current = editor;

    editor.setTheme('ace/theme/monokai');
    editor.session.setMode(`ace/mode/${language}`);
    editor.setReadOnly(isReadOnly);
    editor.setValue(strValue, -1);
    editor.setOptions({
      fontSize: '13px',
      showPrintMargin: false,
      tabSize: 2,
      useSoftTabs: true,
    });

    editor.on('change', () => {
      onChange?.(editor.getValue());
    });

    editor.on('blur', () => {
      onBlur?.();
    });

    return () => {
      editor.destroy();
      editorRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // Sync external value changes
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const currentVal = editor.getValue();
    if (currentVal !== strValue) {
      editor.setValue(strValue, -1);
    }
  }, [strValue]);

  // Sync readOnly changes
  useEffect(() => {
    editorRef.current?.setReadOnly(isReadOnly);
  }, [isReadOnly]);

  if (ace) {
    return (
      <div
        className="code-editor-control"
        data-control-id={control.id}
        ref={containerRef}
        style={{ height, width: '100%', borderRadius: '6px', overflow: 'hidden' }}
      />
    );
  }

  // Fallback textarea
  return (
    <textarea
      id={control.id}
      value={strValue}
      placeholder={control.placeholder ?? `// Enter ${language} code here...`}
      disabled={disabled || control.disabled}
      readOnly={isReadOnly}
      onChange={(e) => onChange?.(e.target.value)}
      onBlur={onBlur}
      spellCheck={false}
      style={{
        width: '100%', height: `${height}px`, resize: 'vertical',
        padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db',
        fontFamily: 'Consolas, Monaco, "Courier New", monospace',
        fontSize: '13px', lineHeight: '1.6', tabSize: 2,
        boxSizing: 'border-box',
      }}
    />
  );
}
